import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { WindowId } from '../../framework/types';
import { useWindows } from '../../framework/WindowManagerContext';
import { useInteraction } from '../../contexts/InteractionContext';
import { InputModalityStore } from '../../../systems/input/InputModalityStore';
import {
  controllerUIInputBridge,
  type ControllerUIInputFrame,
  type ControllerUIResetReason,
} from '../../../systems/input/controllerUIInputBridge';
import {
  ControllerNavigationEngine,
  type ControllerNavigationActions,
  type ControllerNavigationDirection,
} from './ControllerNavigationEngine';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"]):not([aria-disabled="true"])',
].join(',');

interface ControllerNavigationContextValue {
  quickMenuOpen: boolean;
  openQuickMenu(): void;
  closeQuickMenu(): void;
  activateWindow(id: WindowId, currentlyOpen: boolean): void;
}

const ControllerNavigationContext = createContext<ControllerNavigationContextValue | null>(null);
const CONTROLLER_FOCUS_RETRY_MS = 16;

function isPerceivable(element: HTMLElement): boolean {
  let current: HTMLElement | null = element;
  while (current) {
    if (
      current.hidden
      || current.hasAttribute('inert')
      || current.getAttribute('aria-hidden') === 'true'
    ) return false;
    const style = window.getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    current = current.parentElement;
  }
  return true;
}

function focusableElements(root: Element): HTMLElement[] {
  const descendants = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  const elements = root instanceof HTMLElement && root.matches(FOCUSABLE_SELECTOR)
    ? [root, ...descendants]
    : descendants;
  return elements.filter(isPerceivable);
}

function preferredEntry(root: Element): HTMLElement | null {
  const elements = focusableElements(root);
  return elements.find(element => (
    !element.classList.contains('toolwindow-button')
    && !element.getAttribute('aria-label')?.toLowerCase().startsWith('close')
  )) ?? elements[0] ?? null;
}

function controllerWindowSelector(id: WindowId): string {
  return `[data-controller-window-id="${id}"]`;
}

export const ControllerNavigationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { windows, widgets, setEnabled } = useWindows();
  const interaction = useInteraction();
  const [quickMenuOpen, setQuickMenuOpen] = useState(false);
  const quickMenuOpenRef = useRef(false);
  const activeWindowIdRef = useRef<WindowId | null>(null);
  const previousWidgetsRef = useRef(widgets);
  const controllerOwnsFocusRef = useRef(false);
  const focusTimerRef = useRef<number | null>(null);
  const engineRef = useRef(new ControllerNavigationEngine());

  const liveRef = useRef({ windows, widgets, setEnabled, interaction });
  liveRef.current = { windows, widgets, setEnabled, interaction };

  const clearFocusTimer = useCallback(() => {
    if (focusTimerRef.current !== null) {
      window.clearTimeout(focusTimerRef.current);
      focusTimerRef.current = null;
    }
  }, []);

  const focusLater = useCallback((selector: string, preferredSelector?: string) => {
    clearFocusTimer();
    const tryFocus = (retriesRemaining: number) => {
      focusTimerRef.current = null;
      const root = document.querySelector(selector);
      const preferred = root && preferredSelector
        ? root.querySelector<HTMLElement>(preferredSelector)
        : null;
      const target = root
        ? (preferred && isPerceivable(preferred) ? preferred : preferredEntry(root))
        : null;
      if (target) {
        target.focus();
        controllerOwnsFocusRef.current = true;
        return;
      }
      // A closing compact Character sheet can reveal its CSS-suppressed Quick
      // Menu target in the next frame. Retry only a missing/hidden handoff;
      // ordinary NPC/window entry keeps the established immediate timing.
      if (retriesRemaining > 0) {
        focusTimerRef.current = window.setTimeout(
          () => tryFocus(retriesRemaining - 1),
          CONTROLLER_FOCUS_RETRY_MS,
        );
      }
    };
    focusTimerRef.current = window.setTimeout(() => tryFocus(1), 0);
  }, [clearFocusTimer]);

  const openGameplayWindowIds = useCallback((): WindowId[] => {
    const { windows: currentWindows, widgets: currentWidgets } = liveRef.current;
    return currentWindows
      .filter(windowDef => (
        windowDef.kind === 'gameplay'
        && windowDef.category === 'game'
        && currentWidgets[windowDef.id]?.enabled
      ))
      .map(windowDef => windowDef.id);
  }, []);

  const activeGameplayWindowId = useCallback((): WindowId | null => {
    const open = openGameplayWindowIds();
    const active = activeWindowIdRef.current;
    if (active && open.includes(active)) return active;

    const focusedRoot = document.activeElement?.closest<HTMLElement>('[data-controller-window-id]');
    const focusedId = focusedRoot?.dataset.controllerWindowId as WindowId | undefined;
    if (focusedId && open.includes(focusedId)) return focusedId;
    return open[open.length - 1] ?? null;
  }, [openGameplayWindowIds]);

  const activeSurface = useCallback((): Element | null => {
    const { interaction: currentInteraction } = liveRef.current;
    if (currentInteraction.questOfferState.isOpen) {
      return document.querySelector('[data-controller-surface="npc-quests"]');
    }
    if (currentInteraction.shopState.isOpen) {
      return document.querySelector('[data-controller-surface="npc-shop"]');
    }
    if (currentInteraction.interactingNPC) {
      return document.querySelector('[data-controller-surface="npc-dialogue"]');
    }
    const windowId = activeGameplayWindowId();
    if (windowId) return document.querySelector(controllerWindowSelector(windowId));
    if (quickMenuOpenRef.current) {
      return document.querySelector('[data-controller-surface="quick-menu"]');
    }
    return null;
  }, [activeGameplayWindowId]);

  const captureActive = useCallback((): boolean => {
    const { interaction: currentInteraction } = liveRef.current;
    return Boolean(
      currentInteraction.questOfferState.isOpen
      || currentInteraction.shopState.isOpen
      || currentInteraction.interactingNPC
      || openGameplayWindowIds().length > 0
      || quickMenuOpenRef.current,
    );
  }, [openGameplayWindowIds]);

  const focusQuickMenu = useCallback((windowId?: WindowId) => {
    focusLater(
      '[data-controller-surface="quick-menu"]',
      windowId ? `[data-controller-window-launcher="${windowId}"]` : undefined,
    );
  }, [focusLater]);

  const openQuickMenu = useCallback(() => {
    const { interaction: currentInteraction } = liveRef.current;
    const hasDeeperSurface = Boolean(
      currentInteraction.questOfferState.isOpen
      || currentInteraction.shopState.isOpen
      || currentInteraction.interactingNPC
      || openGameplayWindowIds().length > 0,
    );
    quickMenuOpenRef.current = true;
    setQuickMenuOpen(true);
    if (!hasDeeperSurface) focusQuickMenu();
  }, [focusQuickMenu, openGameplayWindowIds]);

  const closeQuickMenu = useCallback(() => {
    quickMenuOpenRef.current = false;
    setQuickMenuOpen(false);
    if (!captureActive()) focusLater('[data-controller-quick-menu-launcher]');
  }, [captureActive, focusLater]);

  const toggleQuickMenu = useCallback(() => {
    if (quickMenuOpenRef.current) closeQuickMenu();
    else openQuickMenu();
  }, [closeQuickMenu, openQuickMenu]);

  const activateWindow = useCallback((id: WindowId, currentlyOpen: boolean) => {
    liveRef.current.setEnabled(id, !currentlyOpen);
    if (currentlyOpen) {
      if (activeWindowIdRef.current === id) activeWindowIdRef.current = null;
      focusQuickMenu(id);
      return;
    }
    activeWindowIdRef.current = id;
    focusLater(controllerWindowSelector(id));
  }, [focusLater, focusQuickMenu]);

  const moveFocus = useCallback((direction: ControllerNavigationDirection) => {
    const root = activeSurface();
    if (!root) return;
    const elements = focusableElements(root);
    if (elements.length === 0) return;

    const current = document.activeElement as HTMLElement | null;
    const index = current ? elements.indexOf(current) : -1;
    if (index < 0) {
      const target = preferredEntry(root);
      target?.focus();
      controllerOwnsFocusRef.current = target !== null;
      return;
    }

    const delta = direction === 'down' || direction === 'right' ? 1 : -1;
    elements[(index + delta + elements.length) % elements.length].focus();
    controllerOwnsFocusRef.current = true;
  }, [activeSurface]);

  const confirm = useCallback(() => {
    const root = activeSurface();
    const current = document.activeElement as HTMLElement | null;
    if (!root || !current || !root.contains(current)) {
      const target = root ? preferredEntry(root) : null;
      target?.focus();
      controllerOwnsFocusRef.current = target !== null;
      return;
    }
    if (
      current.matches('button, a[href], input[type="button"], input[type="submit"], [role="button"]')
      && current.getAttribute('aria-disabled') !== 'true'
      && !(current instanceof HTMLButtonElement && current.disabled)
    ) {
      current.click();
    }
  }, [activeSurface]);

  const back = useCallback(() => {
    const { interaction: currentInteraction } = liveRef.current;
    if (currentInteraction.questOfferState.isOpen) {
      currentInteraction.closeQuestOffer();
      focusLater('[data-controller-surface="npc-dialogue"]', '[data-controller-return="quests"]');
      return;
    }
    if (currentInteraction.shopState.isOpen) {
      currentInteraction.closeShop();
      // `InteractionContext.openShop()` intentionally makes the shop mutually
      // exclusive with its dialogue. If a future NPC flow keeps the dialogue
      // mounted, restore its matching launcher; the current product route
      // returns to the stable in-world menu launcher instead of focusing a
      // node that no longer exists.
      if (currentInteraction.interactingNPC) {
        focusLater('[data-controller-surface="npc-dialogue"]', '[data-controller-return="shop"]');
      } else if (quickMenuOpenRef.current) {
        focusQuickMenu();
      } else {
        focusLater('[data-controller-quick-menu-launcher]');
      }
      return;
    }
    if (currentInteraction.interactingNPC) {
      currentInteraction.closeNPCInteraction();
      if (quickMenuOpenRef.current) focusQuickMenu();
      else focusLater('[data-controller-quick-menu-launcher]');
      return;
    }

    const windowId = activeGameplayWindowId();
    if (windowId) {
      liveRef.current.setEnabled(windowId, false);
      activeWindowIdRef.current = null;
      if (quickMenuOpenRef.current) focusQuickMenu(windowId);
      else focusLater('[data-controller-quick-menu-launcher]');
      return;
    }

    if (quickMenuOpenRef.current) closeQuickMenu();
  }, [activeGameplayWindowId, closeQuickMenu, focusLater, focusQuickMenu]);

  const allowContextualQuestAction = useCallback((): boolean => (
    liveRef.current.interaction.interactingNPC !== null
  ), []);

  const actionsRef = useRef<ControllerNavigationActions>({
    isCaptureActive: captureActive,
    toggleQuickMenu,
    moveFocus,
    confirm,
    back,
    allowContextualQuestAction,
  });
  actionsRef.current = {
    isCaptureActive: captureActive,
    toggleQuickMenu,
    moveFocus,
    confirm,
    back,
    allowContextualQuestAction,
  };

  const releaseControllerFocus = useCallback((_reason?: ControllerUIResetReason | 'disconnect') => {
    clearFocusTimer();
    engineRef.current.reset();
    if (controllerOwnsFocusRef.current) {
      const active = document.activeElement;
      if (active instanceof HTMLElement) active.blur();
    }
    controllerOwnsFocusRef.current = false;
  }, [clearFocusTimer]);

  useEffect(() => controllerUIInputBridge.connect({
    route(frame: ControllerUIInputFrame) {
      if (!frame.connected) releaseControllerFocus('disconnect');
      return engineRef.current.route(frame, actionsRef.current);
    },
    reset(reason) {
      releaseControllerFocus(reason);
    },
  }), [releaseControllerFocus]);

  useEffect(() => {
    const releaseForPointerOrKeyboard = () => releaseControllerFocus();
    window.addEventListener('pointerdown', releaseForPointerOrKeyboard, true);
    window.addEventListener('keydown', releaseForPointerOrKeyboard, true);
    return () => {
      window.removeEventListener('pointerdown', releaseForPointerOrKeyboard, true);
      window.removeEventListener('keydown', releaseForPointerOrKeyboard, true);
    };
  }, [releaseControllerFocus]);

  useEffect(() => {
    const previous = previousWidgetsRef.current;
    const opened = windows.filter(windowDef => (
      windowDef.kind === 'gameplay'
      && windowDef.category === 'game'
      && widgets[windowDef.id]?.enabled
      && !previous[windowDef.id]?.enabled
    ));
    const newest = opened[opened.length - 1];
    if (newest) activeWindowIdRef.current = newest.id;
    previousWidgetsRef.current = widgets;

    const active = activeWindowIdRef.current;
    if (active && !widgets[active]?.enabled) activeWindowIdRef.current = null;
  }, [widgets, windows]);

  useEffect(() => {
    if (InputModalityStore.getSnapshot().modality !== 'gamepad') return;
    if (interaction.questOfferState.isOpen) {
      focusLater('[data-controller-surface="npc-quests"]');
    } else if (interaction.shopState.isOpen) {
      focusLater('[data-controller-surface="npc-shop"]');
    } else if (interaction.interactingNPC) {
      focusLater('[data-controller-surface="npc-dialogue"]');
    }
  }, [
    focusLater,
    interaction.interactingNPC,
    interaction.questOfferState.isOpen,
    interaction.shopState.isOpen,
  ]);

  useEffect(() => () => clearFocusTimer(), [clearFocusTimer]);

  const value = useMemo<ControllerNavigationContextValue>(() => ({
    quickMenuOpen,
    openQuickMenu,
    closeQuickMenu,
    activateWindow,
  }), [activateWindow, closeQuickMenu, openQuickMenu, quickMenuOpen]);

  return (
    <ControllerNavigationContext.Provider value={value}>
      {children}
    </ControllerNavigationContext.Provider>
  );
};

export function useControllerNavigation(): ControllerNavigationContextValue {
  const context = useContext(ControllerNavigationContext);
  if (!context) {
    throw new Error('useControllerNavigation must be used inside ControllerNavigationProvider');
  }
  return context;
}
